import React, { createContext, useEffect, useRef, useState } from 'react';
import { chatbotApiRequests } from 'utils/api-requests/chatbotApiRequests';
import {
    AdContextDtoAdModuleEnum,
    ChatBotMessageDto,
    ChatDetailDto,
    ChatPageDto,
    MessageResponseDto,
    MessageResponseDtoMessageOwnerEnum,
    QuestionCategory,
    SuggestedQuestionRequestDtoAdModuleEnum
} from 'utils/types/ChatbotModels';

interface ExtendedMessageResponseDto extends MessageResponseDto {
    isLocalMessage?: boolean;
}

interface ChatbotContextType {
    isPopupOpen: boolean;
    setPopupOpen: (isOpen: boolean) => void;
    popupMessages: ExtendedMessageResponseDto[];
    popupConversationUuid: string | undefined;
    activeConversation: ChatDetailDto | null;
    conversationHistory: ChatPageDto | null;
    selectedConversationUuid: string | null;
    isLoading: boolean;
    isLoadingMessage: boolean;
    message: string;
    setMessage: (message: string) => void;
    sendMessageInPopup: (message: string) => void;
    sendMessageInHistory: (message: string) => void;
    loadConversationHistory: () => Promise<void>;
    loadConversationDetails: (conversationUuid: string) => Promise<void>;
    startNewConversation: () => void;
    resetPopupChat: () => void;
    deleteConversation: (uuid: string) => Promise<void>;
    startNewPopupChat: () => void;
    isOpenResourcePopUp: boolean;
    closeResourcePopUp: () => void;
    openWithAdContext: (adId: number | string, adModule: string) => Promise<void>;
    suggestedQuestions: string[] | null;
    getMessageAudio: (messageId: number) => Promise<string | null>;
    popupResetKey: number;
}

const initialContextValue: ChatbotContextType = {
    isPopupOpen: false,
    setPopupOpen: () => {},
    popupMessages: [],
    popupConversationUuid: undefined,
    activeConversation: null,
    conversationHistory: null,
    selectedConversationUuid: null,
    isLoading: false,
    isLoadingMessage: false,
    message: '',
    setMessage: () => {},
    sendMessageInPopup: () => {},
    sendMessageInHistory: () => {},
    loadConversationHistory: async () => {},
    loadConversationDetails: async () => {},
    startNewConversation: () => {},
    resetPopupChat: () => {},
    deleteConversation: async () => {},
    startNewPopupChat: () => {},
    isOpenResourcePopUp: false,
    closeResourcePopUp: () => {},
    openWithAdContext: async () => {},
    suggestedQuestions: null,
    getMessageAudio: async () => null,
    popupResetKey: 0
};

const ChatbotContext = createContext<ChatbotContextType>(initialContextValue);

export const ChatbotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isPopupOpen, setPopupOpen] = useState(false);
    const [popupMessages, setPopupMessages] = useState<ExtendedMessageResponseDto[]>([]);
    const [popupConversationUuid, setPopupConversationUuid] = useState<string | undefined>(undefined);

    const [activeConversation, setActiveConversation] = useState<ChatDetailDto | null>(null);
    const [conversationHistory, setConversationHistory] = useState<ChatPageDto | null>(null);
    const [selectedConversationUuid, setSelectedConversationUuid] = useState<string | null>(null);

    const [isLoadingMessage, setIsLoadingMessage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');

    const [isOpenResourcePopUp, setOpenResourcePopUp] = useState(false);
    const [isAutoOpened, setIsAutoOpened] = useState(false);
    const [suggestedQuestions, setSuggestedQuestions] = useState<string[] | null>(null);
    const [pendingAdContext, setPendingAdContext] = useState<{ adId: number; adModule: string } | null>(null);
    const [popupResetKey, setPopupResetKey] = useState(0);
    // eslint-disable-next-line @stylistic/js/no-extra-parens
    const esCleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        return () => {
            esCleanupRef.current?.();
        };
    }, []);

    const closeResourcePopUp = () => {
        setOpenResourcePopUp(false);
    };

    const resetPopupChat = () => {
        setPopupMessages([
            {
                message: 'Cześć! Odpowiem na każde pytanie o flipowaniu, aplikacji i stworzę Ci filtry w apce – wystarczy, że napiszesz, czego potrzebujesz.',
                messageOwner: MessageResponseDtoMessageOwnerEnum.BOT_MESSAGE,
                createdAt: new Date().toISOString(),
                isLocalMessage: true
            }
        ]);
        setPopupConversationUuid(undefined);
    };

    useEffect(() => {
        if (isPopupOpen && !isAutoOpened) {
            loadLatestConversationToPopup();
        }
    }, [isPopupOpen]);

    const startNewPopupChat = () => {
        esCleanupRef.current?.();
        esCleanupRef.current = null;
        setPopupResetKey((k) => k + 1);
        resetPopupChat();
        setMessage('');
        setSuggestedQuestions(null);
        setPendingAdContext(null);
    };

    useEffect(() => {
        const lastOpenDate = localStorage.getItem('chatbotPopupLastOpen');
        const WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;
        const shouldDisplayNewChatPopup = !lastOpenDate || Date.now() - parseInt(lastOpenDate, 10) >= WEEK_IN_MS;

        if (shouldDisplayNewChatPopup) {
            setIsAutoOpened(true);
            resetPopupChat();
            setPopupOpen(true);
            localStorage.setItem('chatbotPopupLastOpen', Date.now().toString());
        }
    }, []);

    const loadConversationHistory = async () => {
        setIsLoading(true);
        const history = await chatbotApiRequests.getConversationHistory();

        if (history) {
            setConversationHistory(history);
        }
        setIsLoading(false);
    };

    const loadConversationDetails = async (conversationUuid: string) => {
        setIsLoading(true);
        setSelectedConversationUuid(conversationUuid);
        const details = await chatbotApiRequests.getConversationDetails(conversationUuid);

        if (details) {
            setActiveConversation(details);
        }
        setIsLoading(false);
    };

    const startNewConversation = () => {
        esCleanupRef.current?.();
        esCleanupRef.current = null;
        setActiveConversation(null);
        setSelectedConversationUuid(null);
        setMessage('');
    };

    const sendMessageInPopup = (messageText: string) => {
        if (!messageText.trim() || isLoadingMessage) return;

        const userMessage: ExtendedMessageResponseDto = {
            message: messageText,
            messageOwner: MessageResponseDtoMessageOwnerEnum.USER_MESSAGE,
            createdAt: new Date().toISOString(),
            isLocalMessage: true
        };

        const streamingBotMessage: ExtendedMessageResponseDto = {
            message: '',
            messageOwner: MessageResponseDtoMessageOwnerEnum.BOT_MESSAGE,
            createdAt: new Date().toISOString(),
            isLocalMessage: true
        };

        setPopupMessages((prev) => [
            ...prev,
            userMessage,
            streamingBotMessage
        ]);
        setIsLoadingMessage(true);
        setMessage('');
        setSuggestedQuestions(null);

        const chatbotMessage: ChatBotMessageDto = {
            message: messageText,
            conversationUuid: popupConversationUuid,
            ...pendingAdContext
                ? {
                    adContext: {
                        adId: pendingAdContext.adId,
                        adModule: pendingAdContext.adModule as AdContextDtoAdModuleEnum
                    }
                }
                : {}
        };

        setPendingAdContext(null);

        esCleanupRef.current?.();
        esCleanupRef.current = chatbotApiRequests.streamAnswer(
            chatbotMessage,
            (chunk) => {
                setPopupMessages((prev) => {
                    const updated = [...prev];
                    const last = updated[updated.length - 1];
                    updated[updated.length - 1] = { ...last, message: last.message + chunk };

                    return updated;
                });
            },
            (conversationUuid, lastBotMessage) => {
                if (conversationUuid) {
                    setPopupConversationUuid(conversationUuid);
                }

                if (lastBotMessage) {
                    setPopupMessages((prev) => {
                        const updated = [...prev];
                        updated[updated.length - 1] = { ...updated[updated.length - 1], ...lastBotMessage };

                        return updated;
                    });
                }

                setIsLoadingMessage(false);
            },
            (errorMessage, errorStatus) => {
                if (errorStatus === 423) {
                    localStorage.setItem('tokenInfoShown', 'true');
                    setOpenResourcePopUp(true);
                }
                setPopupMessages((prev) => {
                    const updated = [...prev];
                    updated[updated.length - 1] = {
                        ...updated[updated.length - 1],
                        message: errorMessage
                    };

                    return updated;
                });
                setIsLoadingMessage(false);
            }
        );
    };

    const sendMessageInHistory = (messageText: string) => {
        if (!messageText.trim() || isLoadingMessage) return;

        const userMessage: ExtendedMessageResponseDto = {
            message: messageText,
            messageOwner: MessageResponseDtoMessageOwnerEnum.USER_MESSAGE,
            createdAt: new Date().toISOString(),
            isLocalMessage: true
        };

        const streamingBotMessage: ExtendedMessageResponseDto = {
            message: '',
            messageOwner: MessageResponseDtoMessageOwnerEnum.BOT_MESSAGE,
            createdAt: new Date().toISOString(),
            isLocalMessage: true
        };

        const baseMessages = activeConversation
            ? [...activeConversation.messages]
            : [];

        setActiveConversation((prev) => ({
            conversationUuid: prev?.conversationUuid ?? '',
            title: prev?.title ?? 'Nowa konwersacja',
            messages: [
                ...baseMessages,
                userMessage,
                streamingBotMessage
            ]
        }));
        setIsLoadingMessage(true);
        setMessage('');

        esCleanupRef.current?.();
        esCleanupRef.current = chatbotApiRequests.streamAnswer(
            {
                message: messageText,
                conversationUuid: selectedConversationUuid || undefined
            },
            (chunk) => {
                setActiveConversation((prev) => {
                    if (!prev) return prev;
                    const messages = [...prev.messages];
                    const last = messages[messages.length - 1];
                    messages[messages.length - 1] = { ...last, message: last.message + chunk };

                    return { ...prev, messages };
                });
            },
            (conversationUuid, lastBotMessage) => {
                if (conversationUuid) {
                    setSelectedConversationUuid(conversationUuid);
                }

                if (lastBotMessage) {
                    setActiveConversation((prev) => {
                        if (!prev) return prev;
                        const messages = [...prev.messages];
                        messages[messages.length - 1] = { ...messages[messages.length - 1], ...lastBotMessage };

                        return { ...prev, messages };
                    });
                }

                loadConversationHistory();
                setIsLoadingMessage(false);
            },
            (errorMessage, errorStatus) => {
                if (errorStatus === 423) {
                    localStorage.setItem('tokenInfoShown', 'true');
                    setOpenResourcePopUp(true);
                }
                setActiveConversation((prev) => {
                    if (!prev) return prev;
                    const messages = [...prev.messages];
                    messages[messages.length - 1] = {
                        ...messages[messages.length - 1],
                        message: errorMessage
                    };

                    return { ...prev, messages };
                });
                setIsLoadingMessage(false);
            }
        );
    };

    const deleteConversation = async (uuid: string) => {
        setIsLoading(true);
        const resp = await chatbotApiRequests.deleteConversation(uuid);

        if (resp) {
            if (selectedConversationUuid === uuid) {
                setSelectedConversationUuid(null);
                setActiveConversation(null);
            }

            await loadConversationHistory();
        }

        setIsLoading(false);
    };

    const openWithAdContext = async (adId: number | string, adModule: string) => {
        const numericAdId = typeof adId === 'string' ? Number(adId) || 0 : adId;

        esCleanupRef.current?.();
        esCleanupRef.current = null;
        setPopupResetKey((k) => k + 1);
        setPopupMessages([]);
        setPopupConversationUuid(undefined);
        setSuggestedQuestions(null);
        setPendingAdContext({ adId: numericAdId, adModule });
        setPopupOpen(true);
        setIsAutoOpened(true);
        setIsLoadingMessage(true);

        const questions = await chatbotApiRequests.getSuggestedQuestions({
            adModule: adModule as SuggestedQuestionRequestDtoAdModuleEnum,
            category: QuestionCategory.PorozmawiajONieruchomosci
        });

        setSuggestedQuestions(questions);
        setIsLoadingMessage(false);
    };

    const loadLatestConversationToPopup = async () => {
        const history = await chatbotApiRequests.getConversationHistory();

        if (history?.newestChat) {
            const details = await chatbotApiRequests.getConversationDetails(history.newestChat.conversationUuid);

            if (details) {
                setPopupMessages(details.messages);
                setPopupConversationUuid(details.conversationUuid);
            } else {
                setPopupMessages(history.newestChat.messages);
                setPopupConversationUuid(history.newestChat.conversationUuid);
            }
        } else {
            resetPopupChat();
        }
    };

    const contextValue: ChatbotContextType = {
        isPopupOpen,
        setPopupOpen,
        popupMessages,
        popupConversationUuid,
        activeConversation,
        conversationHistory,
        selectedConversationUuid,
        isLoading,
        isLoadingMessage,
        message,
        setMessage,
        sendMessageInPopup,
        sendMessageInHistory,
        loadConversationHistory,
        loadConversationDetails,
        startNewConversation,
        resetPopupChat,
        deleteConversation,
        startNewPopupChat,
        isOpenResourcePopUp,
        closeResourcePopUp,
        openWithAdContext,
        suggestedQuestions,
        getMessageAudio: chatbotApiRequests.getMessageAudio,
        popupResetKey
    };

    return (
        <ChatbotContext.Provider value={contextValue}>
            {children}
        </ChatbotContext.Provider>
    );
};

export default ChatbotContext;
